import { lazy } from 'react'
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router'

import { pages, redirects } from '~/app/routes'
import { useGetSurgeryBookmarksQuery, useResourceQueries, useUnscheduledSurgeriesAndMetadataQueries } from '~/hooks/tanstack-query'
import { useLocalizationQuery } from '~/hooks/tanstack-query/entities/core/useLocalizationQuery'
import { useSectionsQuery } from '~/hooks/tanstack-query/entities/core/useSectionsQuery'
import { useWardQuery } from '~/hooks/tanstack-query/entities/core/useWardQuery'
import { useBaseEntitiesQueries } from '~/hooks/tanstack-query/entities/useBaseEntitiesQueries'
import { useFlag } from '~/hooks/useFlag'
import { useOtelLoadingSpanAndMetric } from '~/hooks/useOtelTracker'
import { useWebSocketConnection } from '~/hooks/useWebSocketUpdates/useWebSocketConnection'
import { useStore } from '~/store/store'
import { LazyComponentWrapper } from '~/utils/LazyLoadComponents'

import { MainLayout } from './MainLayout/MainLayout'
import { useCentralizedToasts } from './useCentralizedToasts'
import { useLoadingScreen } from './useLoadingScreen'

// Lazy load all page components with enhanced error handling
const AdminPanel = lazy(() => import('./pages/Admin/AdminPanel.page'))

const DownloadPage = lazy(() => import('./pages/Download/Download.page'))

const MigratePage = lazy(() => import('./pages/Migrate/Migrate.page'))

const NotFoundPage = lazy(() => import('./pages/NotFound/NotFound.page'))

const OperationalPlannerPage = lazy(() => import('./pages/OperationalPlanner/OperationalPlanner.page'))

const PeriodsOverviewPage = lazy(() => import('./pages/PeriodsOverview/PeriodsOverview.page'))
const PeriodDetailPage = lazy(() => import('./pages/PeriodDetailPage/PeriodDetail.page'))

const SurgeonSchedulerPage = lazy(() => import('./pages/SurgeonScheduler/SurgeonScheduler.page'))

const SurgerySchedulerPage = lazy(() => import('./pages/SurgeryScheduler/SurgeryScheduler.page'))

const WaitingListPage = lazy(() => import('./pages/WaitingList/WaitingList.page'))

const InsightsPage = lazy(() => import('./pages/Insights/Insights.page'))

const HospitalConfigurationPage = lazy(() => import('./pages/HospitalConfiguration/HospitalConfiguration.page'))

const AuthenticatedAppContents = () => {
    const landingPage = useStore(state => state.app.activeViews.OPERATIONAL_PLANNER)
    const { show, hide } = useLoadingScreen()
    const { isPending: isBaseEntitiesPending, isError: isBaseEntitiesError } = useBaseEntitiesQueries()
    const isInsightsEnabled = useFlag('insights_page__enabled')
    const isHospitalConfigurationPageEnabled = useFlag('hospital_configuration_page__enabled')
    const isMigrationUtilsEnabled = useFlag('planning_note__migration_utils')

    // The resources, sections, wards, and localizations are considered static and are
    // expected to change very rarely. Hence, we can skip refetching on window focus.

    // Fetch DIPS resources required for surgery booking
    const { isPending: isResourcesPending, isError: isResourcesError } = useResourceQueries({ refetchOnWindowFocus: false, staleTime: Infinity })

    // Fetch DI sections required for fetching unscheduled surgeries
    const {
        isPending: isSectionsPending,
        isError: isSectionsError,
        isSuccess: isSectionsSuccess,
    } = useSectionsQuery({ refetchOnWindowFocus: false, staleTime: Infinity })

    // Fetch DIPS wards and localizations required for fetching unscheduled surgeries
    const { isPending: isWardsPending, isError: isWardsError, isSuccess: isWardsSuccess } = useWardQuery({ refetchOnWindowFocus: false, staleTime: Infinity })
    const {
        isPending: isLocalizationsPending,
        isError: isLocalizationsError,
        isSuccess: isLocalizationsSuccess,
    } = useLocalizationQuery({ refetchOnWindowFocus: false, staleTime: Infinity })

    const initialPending = isBaseEntitiesPending || isSectionsPending || isWardsPending || isResourcesPending || isLocalizationsPending

    useOtelLoadingSpanAndMetric({
        name: 'App.AuthenticatedContents',
        isLoading: initialPending,
    })

    // We don't use isLoading here, because this is more like a pre-fetch in the background to reduce loading times later.
    const isWaitingListArgsNotReady = !(isSectionsSuccess && isWardsSuccess && isLocalizationsSuccess)
    useUnscheduledSurgeriesAndMetadataQueries({ skip: isWaitingListArgsNotReady })

    // Fetch surgery bookmarks for the current user
    useGetSurgeryBookmarksQuery()

    const isApiError = isBaseEntitiesError || isSectionsError
    const isDipsError = isResourcesError || isWardsError || isLocalizationsError
    useCentralizedToasts({ isApiError, isDipsError })

    useWebSocketConnection()

    if (initialPending) {
        show({ progressFraction: 1 })
        return null
    }

    hide()

    const router = createBrowserRouter(
        createRoutesFromElements(
            <>
                <Route path="/" element={<MainLayout />}>
                    <Route path="/" element={<Navigate to={landingPage} replace={true} />} />
                    <Route
                        path={`${pages.PERIODS_OVERVIEW.views.periods.path}/:periodId`}
                        element={
                            <LazyComponentWrapper componentName="Planleggingsperiode detaljer">
                                <PeriodDetailPage />
                            </LazyComponentWrapper>
                        }
                    />
                    <Route
                        path={pages.PERIODS_OVERVIEW.views.periods.path}
                        element={
                            <LazyComponentWrapper componentName="Planleggingsperioder">
                                <PeriodsOverviewPage />
                            </LazyComponentWrapper>
                        }
                    />
                    <Route
                        path={pages.SURGERY_SCHEDULER.views['surgery-scheduler'].path}
                        element={
                            <LazyComponentWrapper componentName="Taktisk plan">
                                <SurgerySchedulerPage />
                            </LazyComponentWrapper>
                        }
                    />

                    <Route
                        path={redirects.SURGERY_SCHEDULER}
                        element={<Navigate to={pages.SURGERY_SCHEDULER.views['surgery-scheduler'].path} replace={true} />}
                    />
                    <Route path={redirects.SURGEON_SCHEDULER} element={<Navigate to={pages.SURGEON_SCHEDULER.views.locations.path} replace={true} />} />
                    {Object.values(pages.SURGEON_SCHEDULER.views).map(({ path }) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                <LazyComponentWrapper componentName="Bemanning">
                                    <SurgeonSchedulerPage />
                                </LazyComponentWrapper>
                            }
                        />
                    ))}
                    <Route path={redirects.PLANNING_PERIODS} element={<Navigate to={pages.PERIODS_OVERVIEW.views.periods.path} replace={true} />} />
                    <Route path={redirects.OPERATIONAL_PLANNER} element={<Navigate to={pages.OPERATIONAL_PLANNER.views.calendar.path} replace={true} />} />
                    <Route path={redirects.AVAILABILITY_VIEW} element={<Navigate to={pages.OPERATIONAL_PLANNER.views.month.path} replace={true} />} />
                    {Object.values(pages.OPERATIONAL_PLANNER.views).map(({ path }) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                <LazyComponentWrapper componentName="Operasjonsprogram">
                                    <OperationalPlannerPage />
                                </LazyComponentWrapper>
                            }
                        />
                    ))}
                    <Route path={redirects.WAITING_LIST} element={<Navigate to={pages.WAITING_LIST.views.unscheduled.path} replace={true} />} />
                    {!isMigrationUtilsEnabled && (
                        <Route path={pages.WAITING_LIST.views.migration.path} element={<Navigate to={pages.WAITING_LIST.views.all.path} replace={true} />} />
                    )}
                    {Object.values(pages.WAITING_LIST.views)
                        .filter(({ path }) => isMigrationUtilsEnabled || path !== pages.WAITING_LIST.views.migration.path)
                        .map(({ path }) => (
                            <Route
                                key={path}
                                path={path}
                                element={
                                    <LazyComponentWrapper componentName="Pasienter">
                                        <WaitingListPage />
                                    </LazyComponentWrapper>
                                }
                            />
                        ))}
                    <Route
                        path={pages.DOWNLOAD.views.download.path}
                        element={
                            <LazyComponentWrapper componentName="Nedlastning">
                                <DownloadPage />
                            </LazyComponentWrapper>
                        }
                    />
                    <Route path={redirects.ADMIN_PANEL} element={<Navigate to={pages.ADMIN_PANEL.views['environment-variables'].path} replace={true} />} />
                    {Object.values(pages.ADMIN_PANEL.views).map(({ path }) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                <LazyComponentWrapper componentName="Adminpanel">
                                    <AdminPanel />
                                </LazyComponentWrapper>
                            }
                        />
                    ))}
                    <Route
                        path={pages.MIGRATE.views.migrate.path}
                        element={
                            <LazyComponentWrapper componentName="Migrering">
                                <MigratePage />
                            </LazyComponentWrapper>
                        }
                    />
                    {isInsightsEnabled &&
                        Object.values(pages.INSIGHTS.views).map(({ path }) => (
                            <Route
                                key={path}
                                path={path}
                                element={
                                    <LazyComponentWrapper componentName="Innsikt">
                                        <InsightsPage />
                                    </LazyComponentWrapper>
                                }
                            />
                        ))}

                    {isHospitalConfigurationPageEnabled && (
                        <>
                            <Route
                                path={redirects.HOSPITAL_CONFIGURATION}
                                element={<Navigate to={pages.HOSPITAL_CONFIGURATION.views.surgeons.path} replace />}
                            />
                            {Object.values(pages.HOSPITAL_CONFIGURATION.views).map(({ path }) => (
                                <Route
                                    key={path}
                                    path={path}
                                    element={
                                        <LazyComponentWrapper componentName="Sykehusoppsett">
                                            <HospitalConfigurationPage />
                                        </LazyComponentWrapper>
                                    }
                                />
                            ))}
                        </>
                    )}
                </Route>
                <Route
                    path="*"
                    element={
                        <LazyComponentWrapper componentName="">
                            <NotFoundPage />
                        </LazyComponentWrapper>
                    }
                />
            </>
        )
    )

    return <RouterProvider router={router} />
}

export default AuthenticatedAppContents
